/**
 * Re-reads something while it is worth re-reading, and not otherwise.
 *
 * Two triggers, because they answer two different questions:
 *   - the tab coming back to the foreground — a phone that was in a pocket
 *     shows whatever it was showing when it went in, and this is both the
 *     cheapest refresh there is and the one a driver notices most;
 *   - a timer, but ONLY while `live` — a screen nothing changes on must not
 *     wake a radio every few minutes. `useNow` expresses the same idea for
 *     the clock ticks, and this is its equivalent for the wire.
 *
 * `intervalMs` belongs to the caller, not here: it should be whatever the
 * server would answer the same thing within. For a flight that is the API's
 * live cache TTL (Cache/CacheItems/FlightLookup), so asking more often than
 * that spends a request to be told what we already know.
 *
 * `refetch` owns its own failure. This hook never retries, never backs off
 * and never surfaces an error: a failed read leaves the caller's last good
 * data exactly where it was, and the next trigger tries again. That is the
 * whole storm policy — a tight retry loop on an airport's wifi is how a
 * driver's battery disappears.
 */
import { useEffect, useRef } from 'react';

export default function useRefetchWhileLive(refetch, {
  enabled = true,
  live = false,
  intervalMs = 0,
} = {}) {
  // The callback closes over the data it refreshes, so it is a new function
  // on every render that changes it. Depending on it would clear the timer
  // and start a fresh interval each time — a beat that keeps being
  // postponed is not a beat. Hold it in a ref and let the interval run.
  const latest = useRef(refetch);
  useEffect(() => {
    latest.current = refetch;
  }, [refetch]);

  // One read at a time. On a bad airport connection an answer can take
  // longer than the interval, and without this the next tick would stack a
  // second request on top of the first, and the one after that a third.
  const reading = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const run = async () => {
      if (reading.current) return;
      reading.current = true;
      try {
        await latest.current?.();
      } catch {
        // The caller's business. See the note above.
      } finally {
        reading.current = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') run();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    // A hidden tab gets a throttled timer anyway, and a driver who is not
    // looking does not need the answer — the handler above fetches it the
    // moment they look again.
    const timer = live && intervalMs > 0
      ? setInterval(() => {
        if (document.visibilityState !== 'hidden') run();
      }, intervalMs)
      : null;

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (timer) clearInterval(timer);
    };
  }, [enabled, live, intervalMs]);
}
